"use client";
import { useTranslations } from "@zapier/i18n";
import { useMemo, useRef } from "react";
import {
  BetaMenuTriggerItem,
  BetaMenuTriggerList,
  BetaMenuTriggerPopover,
  BetaMenuTriggerRoot,
  IconButton,
} from "@zapier/design-system";

import styles from "./BulkActions.module.css";

interface Props<E> {
  assetTypeLabel: string;
  contextMenuCoords: [number, number] | null;
  selectedCount: number;
  selectableCount: number;
  isBulkSelectOpen: boolean;
  setIsBulkSelectOpen: (isOpen: boolean) => void;
  clearSelectedEntities: () => void;
  selectAllEntities: () => void;
  selectableEntities: E[] | undefined;
  setSelectedRows: (selectedRows: string[]) => void;
  closeDropdown: () => void;
  showSelectPaused?: boolean;
}

interface BulkSelectMenuItem {
  onAction: () => void;
  text: string;
}

/**
 * Bulk-select ("Select all") menu.
 *
 * Built on the design system menu (`BetaMenuTriggerList` + `BetaMenuTrigger*` —
 * react-aria Menu + Popover, the same primitive `KebabMenu` uses). This replaced
 * the repo's hand-rolled `ZapDropdown` (since removed) — prefer the DS menu for
 * any new menus. It renders in a portal at `<body>` and owns positioning,
 * dismissal, keyboard, focus, and ARIA, so it can't be clipped by an ancestor's
 * `overflow` / `container-type` containment the way a hand-rolled dropdown can.
 */
export function BulkActionsDropdown<E>({
  assetTypeLabel,
  contextMenuCoords,
  selectedCount,
  selectableCount,
  isBulkSelectOpen,
  setIsBulkSelectOpen,
  selectAllEntities,
  selectableEntities,
  setSelectedRows,
  clearSelectedEntities,
  closeDropdown,
  showSelectPaused,
}: Props<E>) {
  const t = useTranslations("bulk-actions-dropdown");

  // The DS menu normally anchors to its trigger (the caret). For a right-click,
  // we anchor the popover to this zero-size element pinned at the cursor instead,
  // so the menu opens under the pointer like a native context menu.
  const cursorAnchorReference = useRef<HTMLSpanElement>(null);

  const bulkSelectItems = useMemo<BulkSelectMenuItem[]>(() => {
    return [
      selectedCount
        ? { onAction: clearSelectedEntities, text: t("items.clear-selection") }
        : undefined,
      selectedCount < selectableCount
        ? {
            onAction: selectAllEntities,
            text: t("items.select-all", { assetTypeLabel }),
          }
        : undefined,
      showSelectPaused
        ? {
            onAction: () => {
              const pausedIds =
                selectableEntities?.reduce(
                  (accumulator: string[], entity: E) => {
                    if (!(entity as any).isEnabled) {
                      accumulator.push((entity as any).id);
                    }
                    return accumulator;
                  },
                  [],
                ) || [];
              setSelectedRows(pausedIds);
            },
            text: t("items.select-paused"),
          }
        : undefined,
    ].filter(Boolean) as BulkSelectMenuItem[];
  }, [
    assetTypeLabel,
    clearSelectedEntities,
    selectableEntities,
    selectAllEntities,
    selectedCount,
    selectableCount,
    setSelectedRows,
    showSelectPaused,
    t,
  ]);

  return (
    <>
      {contextMenuCoords && (
        <span
          ref={cursorAnchorReference}
          aria-hidden="true"
          className={styles.cursorAnchor}
          style={{
            left: `${contextMenuCoords[0]}px`,
            top: `${contextMenuCoords[1]}px`,
          }}
        />
      )}
      <BetaMenuTriggerRoot
        isOpen={isBulkSelectOpen}
        onOpenChange={(open) => {
          // react-aria drives open/close (trigger press, Escape, outside click,
          // item select). Mirror it into the shared bulk-select state.
          if (open) {
            setIsBulkSelectOpen(true);
          } else {
            closeDropdown();
          }
        }}
      >
        <IconButton
          aria-label={t("accessibility.toggle-bulk-select-menu", {
            assetTypeLabel,
          })}
          data-testid="bulk-actions"
          name="arrowSmallDown"
          size="small"
          variant="ghost"
        />
        <BetaMenuTriggerPopover
          placement="bottom start"
          triggerRef={contextMenuCoords ? cursorAnchorReference : undefined}
        >
          <BetaMenuTriggerList
            aria-label={t("accessibility.bulk-select-menu-label", {
              assetTypeLabel,
            })}
          >
            {bulkSelectItems.map((item) => (
              <BetaMenuTriggerItem key={item.text} onAction={item.onAction}>
                {item.text}
              </BetaMenuTriggerItem>
            ))}
          </BetaMenuTriggerList>
        </BetaMenuTriggerPopover>
      </BetaMenuTriggerRoot>
    </>
  );
}
